import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import config from "../../../common/config";
import { AlertMessage } from "../../../common/data/AlertMsg";
import { ModalMessage } from "../../../common/data/ModalMsg";
import { Routes } from "../../../common/Routes";
import { inProgress, isNullOrUndefined, isNullOrUndefinedOrEmpty } from "../../../common/utils";
import { portalRoot } from "../../../components/authZTable/column-select-pagination-portal";
import { errorMsg, successMsg } from "../../../components/simpleMessageAlert";
import { IsFeatureEnabledOrNot } from "../../../FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "../../../FeatureToggles/toggleList";
import {
  ApplicationInReviewStatus,
  ApplicationListPageActions,
  ApplicationNotInReviewStatus,
  ReviewVerificationPending,
} from "../applicationList/model";
import {
  ApplicationDetailsActions,
  getPinnedColumns,
  getSelectedColumns,
  getTabPageSize,
  getTotalColumns,
  reviewTab,
} from "./model";
import * as service from "./service";
import { AppReviewTasksActions, hasEditedReviewTasks } from "./tabs/dynamic/review/model/model";
import { AppReviewActions, findAllReviewsByAppId } from "./tabs/static/reviewHistory/model";
import { AppSideBarPageActions, SideBarOptions } from "../../../parts/AppSideBar/model";

const { confirm } = Modal;

export const AppDetailController = (appUniqueName) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    value: { data },
    progress,
  } = useSelector((rootState) => rootState.applicationList.applicationList);

  const isLoading = inProgress(progress);

  let appId;
  data?.forEach((app) => {
    if (!isNullOrUndefined(app.uniqueName) && app.uniqueName === appUniqueName) {
      appId = app.id;
    }
  });

  let isAccessRequestEnabled = IsFeatureEnabledOrNot(ToggleList.AccessRequest);
  const isReviewCycleEnabled = IsFeatureEnabledOrNot(ToggleList.ReviewCycle);

  const allReviews = useSelector((rootState) =>
    findAllReviewsByAppId(rootState.appReviewHistory, appId),
  );

  const [activeTabId, setActiveTabId] = useState(null);
  const [isReviewHistoryTabOpenedForFirstTime, setIsReviewHistoryTabOpenedForFirstTime] =
    useState(false);

  const totalColumns = useSelector((rootState) => getTotalColumns(rootState.appDetails, appId));
  const selectedColumns = useSelector((rootState) =>
    getSelectedColumns(rootState.appDetails, appId),
  );
  const pinnedColumns = useSelector((rootState) =>
    getPinnedColumns(rootState.appDetails, appId),
  );

  const tabPageSize = useSelector((rootState) =>
    getTabPageSize(rootState.appDetails, appId, activeTabId),
  );
  const { reviews } = useSelector((rootState) => rootState.appReviewTasks);

  let appDetail = data?.find((it) => it.id === appId);

  const accessRequestMicrofrontendHostUrl = config.accessRequestMicrofrontendUrl;

  const [paginationPortal, setPaginationPortal] = useState(undefined);
  useEffect(() => {
    const updatedPaginationPortal = portalRoot(activeTabId);
    if (updatedPaginationPortal !== undefined && paginationPortal !== updatedPaginationPortal) {
      // new pagination portal found
      setPaginationPortal(updatedPaginationPortal);
    }
  });

  useEffect(() => {
    if (!appDetail) {
      dispatch(ApplicationListPageActions.getApplications());
    }
    dispatch(AppSideBarPageActions.updateSelectedSideBar(SideBarOptions.APPLICATIONS));
  }, [dispatch, appDetail, appUniqueName]);

  useEffect(() => {
    if (isNullOrUndefined(appId) && isLoading === false) {
      history.push(`${Routes.NotFound.path}`);
      errorMsg("This application not exist.");
    }
  }, [appId, isLoading]);

  function appListReviewStatus(status, review) {
    return {
      status: status,
      id: review.reviewId,
      startDate: review.start.startDate,
      endDate: review.end.endDate,
      name: review.name,
      applicationId: appId,
    };
  }

  const latestReview = !isNullOrUndefined(allReviews) ? allReviews[0] : undefined;
  const latestReviewId = !isNullOrUndefined(latestReview) ? latestReview?.reviewId : undefined;
  const latestReviewStatus = !isNullOrUndefined(latestReview) ? latestReview?.status.status : undefined;
  useEffect(() => {
    dispatch(AppReviewTasksActions.updateReviewStatus(allReviews));
    const reviewHistoryLength = allReviews?.length;
    if (allReviews && reviewHistoryLength > 0) {
      let inProgressReview = {};
      let lastClosedReview = {};
      if (allReviews[0].status.status === "In Progress") {
        inProgressReview = appListReviewStatus(ApplicationInReviewStatus, allReviews[0]);
      } else if (allReviews[0].status.status === "Closed & Verification Pending") {
        lastClosedReview = appListReviewStatus(ReviewVerificationPending, allReviews[0]);
      } else {
        lastClosedReview = appListReviewStatus(ApplicationNotInReviewStatus, allReviews[0]);
      }
      if (!isNullOrUndefinedOrEmpty(inProgressReview) && reviewHistoryLength > 1) {
        lastClosedReview = appListReviewStatus(ApplicationNotInReviewStatus, allReviews[1]);
      }
      dispatch(
        ApplicationListPageActions.updateAppStatusOnReviewHistoryChange({
          appId,
          inProgressReview,
          lastClosedReview,
        }),
      );
    }
  }, [dispatch, latestReviewId]);

  function openInProgressReviewTab() {
    if (latestReviewStatus === "In Progress" && isReviewHistoryTabOpenedForFirstTime === false) {
      openActiveReviewTab();
      setIsReviewHistoryTabOpenedForFirstTime(true);
    }
  }

  useEffect(openInProgressReviewTab, [latestReviewId]);

  function refreshAppDetail() {
    dispatch(ApplicationListPageActions.getApplications());
  }

  function refreshReviewHistory() {
    dispatch(AppReviewActions.getReviewHistory(appId));
  }

  function openActiveReviewTab() {
    dispatch(AppReviewActions.enableOpenActiveReview({ appId, enable: true }));
  }

  async function sendGetUserAccessDataEmail(appId, applicationGitsProductTeamEmail) {
    try {
      await service.sendGetUserAccessDataEmail(appId);
      successMsg(
        AlertMessage.requestUserAccessData.successMessage(applicationGitsProductTeamEmail),
      );
    } catch {
      errorMsg(AlertMessage.requestUserAccessData.errorMessage);
    }
  }

  async function startReview(appId) {
    await service.startReview(appId);
  }

  async function closeReview(appId, comment) {
    await service.closeReview(appId, comment);
  }

  async function getReviewTicket() {
    return await service.getReviewTicket(latestReviewId);
  }

  const onReviewClick = (data) => {
    dispatch(AppReviewTasksActions.createReviewTaskIfNotExits(data));
  };

  const setMyListSwitch = (payload) => {
    dispatch(ApplicationDetailsActions.setMyListSwitch({ ...payload, appId }));
  };

  const setSelectedColumns = (selectedColumns) => {
    dispatch(ApplicationDetailsActions.setActiveColumns({ selectedColumns, appId }));
  };

  const closeActiveReviewTab = () => {
    dispatch(AppReviewTasksActions.removeActiveReview({ appId }));
  };

  useEffect(() => {
    if (activeTabId !== reviewTab.id && !isNullOrUndefined(activeTabId)) {
      refreshReviewHistory();
    }
  }, [appId, activeTabId]);

  const setTabPageSize = ({ pageSize }) => {
    dispatch(
      ApplicationDetailsActions.setTabPageSize({
        appId,
        tabId: activeTabId,
        pageSize,
      }),
    );
  };

  const onTabPaneRemove = (reviewId) => {
    const closeReviewTab = () => {
      dispatch(AppReviewTasksActions.removeReviewTaskIfExists({ reviewId }));
      dispatch(AppReviewTasksActions.removeEditedReviewTasks({ reviewId }));
      dispatch(AppReviewTasksActions.removeSubmittedReviewTaskIfExists({ reviewId }));
      dispatch(AppReviewTasksActions.removeBatchSelectedReviewTasks({ reviewId }));
      setMyListSwitch({ tabId: reviewId, active: false, show: false });
      setActiveTabId(reviewTab.id);
    };

    const reviewDetails = reviews.filter((review) => review.reviewId === reviewId)[0];
    if (hasEditedReviewTasks(reviewId) && reviewDetails.status === "In Progress") {
      confirm({
        title: ModalMessage.closeTabWithEditedEntries.title,
        content: ModalMessage.closeTabWithEditedEntries.confirmationMsg,
        icon: <ExclamationCircleOutlined />,
        onOk: closeReviewTab,
        onCancel: () => {
        },
      });
    } else {
      closeReviewTab();
    }
  };

  return {
    appId,
    appDetail,
    isLoading,
    refreshAppDetail,
    sendGetUserAccessDataEmail,
    startReview,
    closeReview,
    reviews,
    onTabPaneRemove,
    setActiveTabId,
    setMyListSwitch,
    activeTabId,
    refreshReviewHistory,
    openActiveReviewTab,
    closeActiveReviewTab,
    tabPageSize,
    setTabPageSize,
    paginationPortal,
    totalColumns,
    selectedColumns,
    pinnedColumns,
    setSelectedColumns,
    onReviewClick,
    accessRequestMicrofrontendHostUrl,
    isAccessRequestEnabled,
    getReviewTicket,
    isReviewCycleEnabled,
    latestReviewId,
  };
};